import { Modal } from '../../../components/modal/modal';
import { useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { FieldLabel, FileField } from '../../../components/field/field';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { useImportExportApi } from '../../../clients/quipli-service/import-export/import-export-api';
import { Typography } from '../../../components/typography/typography';
import { Radio } from '../../../components/radio/radio';

export const PartImportExportModal = ({
  isOpen,
  isImport,
  setIsOpen,
  onFileUpload,
  onFileError,
}: {
  isOpen: boolean;
  isImport: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFileUpload: () => void;
  onFileError: (message: string) => void;
}) => {
  const importExportAPI = useImportExportApi();

  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const [isStockAdditive, setIsStockAdditive] = useState<boolean>(true);

  // Callbacks
  const exportParts = async () => {
    setRequestInProgress(true);
    const file = await importExportAPI.exportParts();

    const exportFileName = `Parts_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const importParts = async () => {
    if (!file) return;

    setRequestInProgress(true);

    if (file) {
      try {
        await importExportAPI.importParts(file, isStockAdditive);
        onFileUpload();
      } catch (error) {
        onFileError('Error importing parts');
        setRequestInProgress(false);
        return;
      }
    }

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const clear = () => {
    setFile(undefined);
  };

  const isButtonDisabled = (): boolean => {
    return isRequestInProgress || (isImport && !file);
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    clear();
    setIsOpen(false);
  };

  const exportSampleFile = async () => {
    const file = await importExportAPI.exportPartsSample();

    const exportFileName = `Parts_Sample_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isImport ? 'Import Parts' : 'Export Parts'}
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isButtonDisabled()}
              onClick={() => (isImport ? importParts() : exportParts())}
            >
              {isImport ? 'Import File' : 'Export'}
            </button>
          </>
        )
      }
      close={closeModal}
    >
      <div>
        {isImport && (
          <div className="text-center px-4 mb-4">
            <Typography type="text-sm" className="text-grey-500">
              <div>Import parts from a XLS or XLSX spreadsheet file.</div>
              <div>
                Click{' '}
                <a className="text-quipli-black-800 underline cursor-pointer" onClick={exportSampleFile}>
                  here
                </a>{' '}
                to see the exact format of the worksheets.
              </div>
            </Typography>
          </div>
        )}

        {/* Stock Type */}
        {isImport && (
          <div className="flex flex-col mt-4">
            <FieldLabel label="Sale Stock Type" isRequired />
            <div className="mt-2">
              <Radio
                name="stock-type"
                isChecked={isStockAdditive}
                onChange={(isChecked) => setIsStockAdditive(isChecked)}
                label="Add to existing location stock"
                type="white"
                className="mb-1"
              />
              <Radio
                name="stock-type"
                isChecked={!isStockAdditive}
                onChange={(isChecked) => setIsStockAdditive(!isChecked)}
                label="Replace existing location stock"
                type="white"
              />
            </div>
          </div>
        )}

        {/* File */}
        {isImport && (
          <div className="mt-4">
            <FileField
              className="w-full h-full resize-none text-sm"
              label="File"
              value={file}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(newFile) => setFile(newFile as File)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
